import type { RefObject, MutableRefObject } from "react";
import type { TFunction } from "i18next";
import { CheckCircle2, Shield, Briefcase } from "lucide-react";
import { cn } from "@/lib/utils";
import { PhoneVerification } from "@/components/verification/PhoneVerification";
import { USER_PLACEHOLDER_AVATAR_URL } from "@/lib/site-assets";
import type { OccasionDef } from "@/lib/occasions";

type CarInfo = {
  ownerName?: string | null;
  ownerImage?: string | null;
  ownerSinceYear?: number | null;
} | null;

export function CheckoutGuestSection({
  t,
  phoneRequired,
  phoneGateBlocking,
  phoneVerifiedClient,
  phoneVerified,
  phoneVerificationError,
  phoneVerificationRef,
  initialPhoneForPanel,
  selectedInsuranceId,
  onPhoneVerified,
  carInfo,
  messageToOwner,
  setMessageToOwner,
  onMessageToOwnerBlur,
  messageToOwnerError,
  messageToOwnerChecking,
  showOccasionPicker,
  occasionOptions,
  occasionParam,
  onSelectOccasion,
  occasionError,
  occasionSectionRef,
  workTrip,
  setWorkTrip,
  businessName,
  setBusinessName,
  businessNameTouchedRef,
  vatNumber,
  setVatNumber,
  defaultBusinessName,
}: {
  t: TFunction;
  phoneRequired: boolean;
  phoneGateBlocking: boolean;
  phoneVerifiedClient: boolean;
  phoneVerified: boolean;
  phoneVerificationError: string | null;
  phoneVerificationRef: RefObject<HTMLDivElement | null>;
  initialPhoneForPanel: string | null;
  selectedInsuranceId: number | null;
  onPhoneVerified: () => void;
  carInfo: CarInfo;
  messageToOwner: string;
  setMessageToOwner: (v: string) => void;
  onMessageToOwnerBlur?: () => void;
  messageToOwnerError?: string | null;
  messageToOwnerChecking?: boolean;
  showOccasionPicker: boolean;
  occasionOptions: OccasionDef[];
  occasionParam: string | undefined;
  onSelectOccasion: (slug: string) => void;
  occasionError?: string | null;
  occasionSectionRef?: RefObject<HTMLDivElement | null>;
  workTrip: boolean;
  setWorkTrip: React.Dispatch<React.SetStateAction<boolean>>;
  businessName: string;
  setBusinessName: (v: string) => void;
  businessNameTouchedRef: MutableRefObject<boolean>;
  vatNumber: string;
  setVatNumber: (v: string) => void;
  defaultBusinessName: string | null;
}) {
  return (
    <>
      {/* Task #1006: Inline phone verification — rendered at the top of
            the left column so the renter sees the gate before scrolling
            to payment. Required for every rental kind (driving /
            chauffeured / event) so hosts can reach the renter for
            pickup / coordination. The panel renders only when the
            matrix says we need to gate AND the renter hasn't yet
            verified — once they finish the OTP flow, the panel
            collapses to a verified confirmation banner and the Pay
            button below unlocks. We deliberately render this inline
            rather than redirecting to /settings so the renter never
            loses any in-progress checkout state. */}
      {phoneRequired && (phoneGateBlocking || phoneVerifiedClient) && (
        <section
          ref={phoneVerificationRef}
          id="checkout-phone-verification-section"
          data-testid="checkout-phone-verification"
          className={cn(
            "rounded-xl border p-5",
            phoneVerified ? "border-green-200 bg-green-50/40" : "border-border",
            phoneVerificationError && !phoneVerified && "border-destructive/40 bg-destructive/5",
          )}
        >
          <div className="flex items-start gap-3">
            {phoneVerified ? (
              <CheckCircle2 className="mt-0.5 h-5 w-5 text-green-600" />
            ) : (
              <Shield className="mt-0.5 h-5 w-5 text-foreground" />
            )}
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold text-foreground">
                {phoneVerified
                  ? t("checkout.phone_verified_heading", "Phone verified")
                  : t("checkout.phone_verification_heading", "Verify your phone number")}
              </h2>
              <p className="mt-0.5 text-xs text-muted-foreground">
                {phoneVerified
                  ? t("checkout.phone_verified_subtitle", "Your host can now reach you about pickup and trip details.")
                  : t("checkout.phone_verification_subtitle", "Hosts use this number to confirm pickup details and reach you during the trip. We'll text a 6-digit code.")}
              </p>
            </div>
          </div>
          {!phoneVerified && (
            <>
              {phoneVerificationError && (
                <p
                  role="alert"
                  data-testid="checkout-phone-verification-error"
                  className="mt-3 text-xs text-destructive"
                >
                  {phoneVerificationError}
                </p>
              )}
              <div className="mt-4">
                <PhoneVerification
                  initialPhone={initialPhoneForPanel}
                  onVerified={onPhoneVerified}
                />
              </div>
            </>
          )}
        </section>
      )}

      {/* Message to the host (optional) */}
      <section id="checkout-message-to-host-section" className="rounded-xl border border-border p-5">
        <div className="flex items-start gap-3">
          {/* Hosts who haven't uploaded a profile photo render the
              branded R2-hosted "no photo" SVG instead of the older
              initial-letter fallback — matches DRIVESHARE's marketing
              treatment so the checkout page never shows a single
              uppercase letter where a real face would normally live. */}
          <img
            src={carInfo?.ownerImage || USER_PLACEHOLDER_AVATAR_URL}
            alt={carInfo?.ownerName || t("checkout.host_avatar_alt")}
            className="h-12 w-12 shrink-0 rounded-full object-cover bg-muted"
          />
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold text-foreground">
              {carInfo?.ownerName
                ? t("checkout.message_to_host_heading", { name: carInfo.ownerName })
                : t("checkout.message_to_host_heading_default")}
            </h2>
            {carInfo?.ownerSinceYear ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {t("checkout.hosting_since", { year: carInfo.ownerSinceYear })}
              </p>
            ) : null}
          </div>
        </div>
        {/* Airbnb-style "what to share" prompt — gives the renter a
            concrete checklist (why the car, who's coming, where to)
            so the message box doesn't feel like a blank stare. */}
        <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
          {carInfo?.ownerName
            ? t("checkout.message_to_host_subtitle_named", { name: carInfo.ownerName })
            : t("checkout.message_to_host_subtitle")}
        </p>
        <div className="mt-3">
          <label htmlFor="message-to-host" className="sr-only">
            {t("checkout.message_to_host_label")}
          </label>
          <textarea
            id="message-to-host"
            value={messageToOwner}
            onChange={(e) => setMessageToOwner(e.target.value.slice(0, 1000))}
            onBlur={onMessageToOwnerBlur}
            maxLength={1000}
            rows={4}
            aria-invalid={messageToOwnerError ? true : undefined}
            aria-describedby={messageToOwnerError ? "message-to-host-error" : undefined}
            placeholder={
              carInfo?.ownerName
                ? t("checkout.message_to_host_placeholder_named", { name: carInfo.ownerName })
                : t("checkout.message_to_host_placeholder")
            }
            className={cn(
              "block w-full resize-y rounded-md border bg-background px-3 py-2 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent",
              messageToOwnerError ? "border-destructive/60" : "border-border",
            )}
          />
          {/* Task #3487 — inline moderation error (shown when the note is
              hard-suppressed by the shared pipeline). Cleared as soon as the
              renter edits the note. */}
          {messageToOwnerError ? (
            <p
              role="alert"
              id="message-to-host-error"
              data-testid="checkout-message-to-host-error"
              className="mt-1.5 text-xs text-destructive"
            >
              {messageToOwnerError}
            </p>
          ) : (
            <p className="mt-1.5 text-xs text-muted-foreground">
              {messageToOwnerChecking
                ? t("checkout.message_to_host_checking", "Checking your message…")
                : t("checkout.message_to_host_help")}
            </p>
          )}
        </div>
      </section>

      {/* What's this trip for?
          Required occasion picker for chauffeur / event rentals — captured
          here on the Confirm-and-pay page since the booking widget no
          longer asks (per-listing event_types config was removed from the
          wizard) and the post-booking confirmation page only displays the
          chosen value, it doesn't capture one. The selection is written
          into secure checkout state and the checkout draft so it survives
          auth handoff, back navigation, and payment retries. */}
      {showOccasionPicker && (
        <section
          id="checkout-occasion-section"
          ref={occasionSectionRef}
          tabIndex={-1}
          className={cn("rounded-xl border p-5", occasionError ? "border-destructive/60" : "border-border")}
          data-testid="section-occasion-picker"
          aria-invalid={occasionError ? true : undefined}
          aria-describedby={occasionError ? "checkout-occasion-error" : undefined}
        >
          <h2 className="text-base font-semibold text-foreground">
            {t("checkout.occasion_section_title")}
            <span className="ml-2 text-xs font-normal text-muted-foreground">{t("common.optional", "Optional")}</span>
          </h2>
          <p className="mt-1 text-xs text-muted-foreground">
            {t("checkout.occasion_section_subtitle")}
          </p>
          <div
            className="mt-4 grid grid-cols-2 gap-2 sm:grid-cols-3"
            role="group"
            aria-label={t("checkout.occasion_section_title")}
            aria-invalid={occasionError ? true : undefined}
            aria-describedby={occasionError ? "checkout-occasion-error" : undefined}
          >
            {occasionOptions.map((occ) => {
              const selected = occasionParam === occ.slug;
              const Icon = occ.Icon;
              return (
                <button
                  key={occ.slug}
                  type="button"
                  onClick={() => onSelectOccasion(occ.slug)}
                  aria-pressed={selected}
                  data-testid={`occasion-tile-${occ.slug}`}
                  className={cn(
                    "flex items-center gap-2.5 rounded-lg border px-3 py-2.5 text-left text-sm transition-colors focus:outline-none focus:ring-2 focus:ring-accent focus:ring-offset-1",
                    selected
                      ? "border-foreground bg-foreground/5 text-foreground"
                      : "border-border text-foreground hover:border-foreground/40 hover:bg-muted/40",
                  )}
                >
                  <Icon
                    className={cn(
                      "h-5 w-5 shrink-0",
                      selected ? "text-foreground" : "text-muted-foreground",
                    )}
                    aria-hidden="true"
                  />
                  <span className="font-medium leading-tight">
                    {t(`occasions.${occ.slug}`, occ.label)}
                  </span>
                </button>
              );
            })}
          </div>
          {occasionError && (
            <p id="checkout-occasion-error" role="alert" data-testid="checkout-occasion-error" className="mt-2 text-xs text-destructive">
              {occasionError}
            </p>
          )}
        </section>
      )}

      {/* 1. Booking for business */}
      <section className="rounded-xl border border-border p-5">
        <button
          type="button"
          onClick={() => {
            setWorkTrip((v) => {
              const next = !v;
              // Pre-fill from the saved profile default the first time the
              // renter toggles this on, but only if they haven't already
              // typed something themselves on this checkout.
              if (next && !businessNameTouchedRef.current && !businessName && defaultBusinessName) {
                setBusinessName(defaultBusinessName);
              }
              return next;
            });
          }}
          aria-pressed={workTrip}
          className="flex w-full items-center justify-between gap-4 text-left"
        >
          <div className="flex items-start gap-3">
            <Briefcase className="mt-0.5 h-5 w-5 text-muted-foreground" />
            <div>
              <h2 className="text-base font-semibold text-foreground">{t("checkout.business_title")}</h2>
              <p className="mt-0.5 text-xs text-muted-foreground">{t("checkout.business_subtitle")}</p>
            </div>
          </div>
          <span
            className={cn(
              "relative inline-flex h-6 w-11 shrink-0 rounded-full border-2 border-transparent transition-colors",
              workTrip ? "bg-foreground" : "bg-gray-300 dark:bg-gray-600"
            )}
          >
            <span className={cn(
              "pointer-events-none mt-0.5 ml-0.5 inline-block h-4 w-4 rounded-full bg-white shadow-sm transition-transform",
              workTrip ? "translate-x-5" : "translate-x-0"
            )} />
          </span>
        </button>
        {workTrip && (
          <div className="mt-4 space-y-3">
            <div>
              <label htmlFor="business-name" className="text-sm font-medium text-foreground">
                {t("checkout.business_name_label")} <span className="text-xs font-normal text-muted-foreground">{t("checkout.optional_label")}</span>
              </label>
              <input
                id="business-name"
                type="text"
                value={businessName}
                onChange={(e) => { businessNameTouchedRef.current = true; setBusinessName(e.target.value.slice(0, 120)); }}
                maxLength={120}
                placeholder={t("checkout.business_name_placeholder")}
                className="mt-1.5 block w-full rounded-md border border-border bg-background px-3 py-2 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <div>
              <label htmlFor="vat-number" className="text-sm font-medium text-foreground">
                {t("checkout.vat_number_label", "Tax ID")} <span className="text-xs font-normal text-muted-foreground">{t("checkout.optional_label")}</span>
              </label>
              <input
                id="vat-number"
                type="text"
                value={vatNumber}
                onChange={(e) => setVatNumber(e.target.value.slice(0, 40))}
                maxLength={40}
                placeholder={t("checkout.vat_number_placeholder", "Enter your tax ID")}
                className="mt-1.5 block w-full rounded-md border border-border bg-background px-3 py-2 text-base md:text-sm text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-accent"
              />
            </div>
            <p className="text-xs text-muted-foreground">{t("checkout.business_appears_on_invoice")}</p>
          </div>
        )}
      </section>
    </>
  );
}
